/**
 * template-preview-controller — the one place All Journeys keeps custom HTML template
 * context while it authors the Event List Page and Event Landing Page tabs.
 *
 * WHY IT EXISTS. The template panel only mounts in each tab's General settings, but the
 * right-hand preview, the header and the phone Settings/Preview switch all need to know
 * which design they are showing. So the selection (tab, target, event, unapplied pick),
 * the reads it depends on, an Apply in progress and the server-composed preview all live
 * here, owned by the authoring Designer for its whole life. Changing rail section or
 * switching the phone pane unmounts the panel; it never loses this state.
 *
 * Data: bound. Reads and writes go through ./template-data (the typed api-client):
 * listPortalExperiencePages · getPortalSettings · setPortalEventListTemplate ·
 * putPortalSettings · getStudioEvent · updateEvent (creation) · previewPortalExperiencePage
 * · the public events read for the catalogue. Nothing is stored in the browser.
 *
 * RACES. Pages, settings, the catalogue and each event are read under their own sequence
 * numbers, and only the newest read of each kind is applied. Apply invalidates every
 * settings (or that event's) read still in flight when its canonical read-back lands, so an
 * older read can never put the old value back. A preview's request key names the whole
 * context (tab, target, event, template, event slug, data revision); an answer is applied
 * only while the CURRENT selection still asks for exactly that key, and a context that asks
 * for no preview at all (standard design, no template, no event) drops anything pending.
 * The last page that composed is kept only for the SAME context (tab, target, event,
 * template) and is then marked stale; a different context never shows another's page.
 *
 * PREVIEWS ARE NOT LIVE. A preview of a pick that is not applied says so. Only Apply (with
 * its own confirmation in the panel) changes what visitors see; this module never applies
 * anything on its own.
 */
import React from "react";
import type { PortalExperiencePageSummary, PortalSettings, PortalTemplateFinding } from "@v0y4ge/api-contracts";
import { t as i18nT } from "../../../lib/i18n";
import { toast } from "../../../shell/toast";
import {
  EVENT_LIST_SLOT, applyDefaultDetailTemplate, applyEventDetailTemplate, applyListTemplate, listAllPages,
  listCarriesLiveLayout, loadTemplateEventCatalog, previewPage, readEventTemplate, readSettings, templateCandidates,
  tf, toTemplateError, type EventTemplateState, type PreviewResult, type TemplateEventOption,
} from "./template-data";

/** The dropdown value meaning "no custom template" (the standard page). */
export const STANDARD_VALUE = "__standard__";
/** The dropdown value meaning "this event uses the default for all events". */
export const INHERIT_VALUE = "__inherit__";

export type TemplateTab = "events" | "landing";
export type TemplateTarget = "list" | "default" | "event";
export type LandingScope = "default" | "event";
/** What the person asked the preview to show. "auto": the custom template when one is in use. */
export type ViewPreference = "auto" | "standard" | "custom";

type ReadStatus = "idle" | "loading" | "error" | "ready";
interface ReadState { status: ReadStatus; message?: string }

export interface EventRead { status: ReadStatus; data?: EventTemplateState; message?: string }

/** Exactly what one Apply will change, fixed when it is confirmed. */
export interface ApplySnapshot {
  target: TemplateTarget;
  eventId: string;
  eventName: string;
  /** The template slug to set, or null to clear. */
  slug: string | null;
  /** What the person sees named in the confirmation. */
  label: string;
}

/** A pick made in the template dropdown, valid only for the context it was made in. */
interface Pick { context: string; value: string }

interface PreviewSlot {
  requestKey: string | null;
  contextKey: string | null;
  status: "idle" | "loading" | "ready" | "error";
  /** The server's answer to `requestKey` (it may carry findings and no page). */
  answer: PreviewResult | null;
  message: string | null;
  /** The last answer that carried a page, and the context it belongs to. */
  lastGood: { contextKey: string; result: PreviewResult } | null;
}

export interface TemplateControllerState {
  /** The tab being authored, or null on any other tab. */
  tab: TemplateTab | null;
  reads: { pages: ReadState; settings: ReadState };
  /** Pages and settings together: error if either failed, loading if either is loading. */
  data: ReadState;
  pages: PortalExperiencePageSummary[];
  settings: PortalSettings | null;
  /** Bumped when pages and settings have both been (re-)read, and by every Apply read-back; part of the preview key. */
  revision: number;
  catalog: { status: ReadStatus; events: TemplateEventOption[]; message?: string };
  events: Record<string, EventRead>;
  list: { view: ViewPreference; pick: Pick | null };
  landing: { view: ViewPreference; scope: LandingScope; eventId: string; pick: Pick | null };
  applying: ApplySnapshot | null;
  applyError: { snapshot: ApplySnapshot; message: string } | null;
  preview: PreviewSlot;
}

/** The typed selection the preview, header and panel all read. */
export interface TemplateSelection {
  tab: TemplateTab;
  target: TemplateTarget;
  /** The real event: the one being given a template ("event"), or the one a default is shown with ("exemplar"). */
  event: TemplateEventOption | null;
  eventRole: "event" | "exemplar" | null;
  /** The dropdown value (a template slug, STANDARD_VALUE or INHERIT_VALUE). */
  value: string;
  /** The value stored for this target now, read back from the server. */
  appliedValue: string;
  /** The template slug stored for this target, or null. */
  appliedSlug: string | null;
  /** The template the dropdown value renders (INHERIT resolves to the default), or null for the standard page. */
  effectiveSlug: string | null;
  /** The template assigned to this context now (event: its own, else the default), or null for the standard page. */
  inUseSlug: string | null;
  /** False when that template is not published right now, so visitors are served the fallback instead. */
  inUseAvailable: boolean;
  /** True when the dropdown value is not what is applied. */
  candidate: boolean;
  /** "All events" only: the example event's own template, which a default never overrides. */
  exemplarOverride: string | null;
  /** Whether the reads this selection depends on have answered. */
  ready: boolean;
}

export interface TemplatePreviewView {
  /** What the right-hand canvas shows. */
  mode: "standard" | "custom";
  /** Whether the dropdown value is a custom template that can be shown at all. */
  customAvailable: boolean;
  selection: TemplateSelection | null;
  /** True when the template to show is not published right now (the server says so). */
  notPublished: boolean;
  /** What the custom canvas has to show. */
  preview: {
    status: "idle" | "loading" | "ready" | "error";
    /** The page shown: the current answer's, or the last good one of this same context. */
    html: string | null;
    /** True when the page shown is not the current answer (it is an earlier one for this context). */
    stale: boolean;
    /** The current answer's findings, or that it composed no page. Null when it composed cleanly. */
    diagnostics: { errors: PortalTemplateFinding[]; composed: boolean } | null;
    message: string | null;
    /** True when a preview needs an event and none can be chosen yet. */
    needsEvent: boolean;
  };
  templateTitle: (slug: string | null | undefined) => string | null;
}

interface PreviewRequest { requestKey: string; contextKey: string; slot: string; templateSlug: string; eventSlug: string | null }

const PREVIEW_IDLE: PreviewSlot = { requestKey: null, contextKey: null, status: "idle", answer: null, message: null, lastGood: null };

function initialState(): TemplateControllerState {
  return {
    tab: null,
    reads: { pages: { status: "idle" }, settings: { status: "idle" } },
    data: { status: "idle" },
    pages: [],
    settings: null,
    revision: 0,
    catalog: { status: "idle", events: [] },
    events: {},
    list: { view: "auto", pick: null },
    landing: { view: "auto", scope: "default", eventId: "", pick: null },
    applying: null,
    applyError: null,
    preview: PREVIEW_IDLE,
  };
}

function combineReads(r: { pages: ReadState; settings: ReadState }): ReadState {
  if (r.pages.status === "error" || r.settings.status === "error") {
    return { status: "error", message: r.pages.status === "error" ? r.pages.message : r.settings.message };
  }
  if (r.pages.status === "loading" || r.settings.status === "loading") return { status: "loading" };
  if (r.pages.status === "ready" && r.settings.status === "ready") return { status: "ready" };
  return { status: r.pages.status === "idle" && r.settings.status === "idle" ? "idle" : "loading" };
}

export class TemplatePreviewController {
  private state: TemplateControllerState = initialState();
  private listeners = new Set<() => void>();
  private seq = { pages: 0, settings: 0, catalog: 0, preview: 0, events: {} as Record<string, number> };
  /** A tab event offered before the catalogue arrived; used once, as the first choice. */
  private seedEventId: string | null = null;
  private viewCache: { state: TemplateControllerState; view: TemplatePreviewView } | null = null;

  /* ── Store ─────────────────────────────────────────────────────────── */

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };

  getState = (): TemplateControllerState => this.state;

  private set(patch: Partial<TemplateControllerState>) {
    this.state = { ...this.state, ...patch };
    this.syncPreview();
    this.listeners.forEach((fn) => fn());
  }

  /** Record a pages/settings read. Both having answered after a refresh bumps the data revision. */
  private setReads(patch: Partial<TemplateControllerState["reads"]>, extra: Partial<TemplateControllerState> = {}) {
    const reads = { ...this.state.reads, ...patch };
    const data = combineReads(reads);
    const becameReady = data.status === "ready" && this.state.data.status !== "ready" && extra.revision === undefined;
    this.set({ ...extra, reads, data, ...(becameReady ? { revision: this.state.revision + 1 } : {}) });
  }

  /* ── Tab ───────────────────────────────────────────────────────────── */

  /**
   * Follow the Designer's tab. `tabEventId` is the event that tab shows; it is only a first
   * choice for the catalogue, never the preview's event once the catalogue has answered.
   */
  activate(tab: TemplateTab | null, tabEventId?: string | null) {
    if (tabEventId && !this.state.landing.eventId && this.state.catalog.status !== "ready") this.seedEventId = tabEventId;
    if (tab === this.state.tab) return;
    this.seq.preview++;
    this.set({ tab, preview: PREVIEW_IDLE });
    if (!tab) return;
    if (this.state.data.status === "idle") this.refresh();
    if (tab === "landing" && this.state.catalog.status === "idle") this.loadCatalog();
  }

  /* ── Reads ─────────────────────────────────────────────────────────── */

  /** Re-read pages and settings (also after the template library closes, so a new publish shows). */
  refresh = () => {
    const ps = ++this.seq.pages;
    const ss = ++this.seq.settings;
    this.setReads({ pages: { status: "loading" }, settings: { status: "loading" } });
    listAllPages()
      .then((pages) => { if (ps === this.seq.pages) this.setReads({ pages: { status: "ready" } }, { pages }); })
      .catch((err) => { if (ps === this.seq.pages) this.setReads({ pages: { status: "error", message: toTemplateError(err).message } }); });
    readSettings()
      .then((settings) => { if (ss === this.seq.settings) this.setReads({ settings: { status: "ready" } }, { settings }); })
      .catch((err) => { if (ss === this.seq.settings) this.setReads({ settings: { status: "error", message: toTemplateError(err).message } }); });
    const id = this.state.landing.eventId;
    if (id && this.state.events[id]) this.loadEvent(id);
  };

  /** The full published catalogue (ended, sold-out and closed events included). Keeps the events already shown. */
  loadCatalog = () => {
    const seq = ++this.seq.catalog;
    this.set({ catalog: { ...this.state.catalog, status: "loading", message: undefined } });
    loadTemplateEventCatalog()
      .then((events) => {
        if (seq !== this.seq.catalog) return;
        const cur = this.state.landing.eventId;
        const seed = this.seedEventId && events.some((e) => e.id === this.seedEventId) ? this.seedEventId : "";
        const eventId = cur && events.some((e) => e.id === cur) ? cur : seed || events[0]?.id || "";
        this.seedEventId = null;
        this.set({ catalog: { status: "ready", events }, landing: { ...this.state.landing, eventId } });
        if (eventId) this.ensureEvent(eventId);
      })
      .catch((err) => {
        if (seq === this.seq.catalog) this.set({ catalog: { ...this.state.catalog, status: "error", message: toTemplateError(err).message } });
      });
  };

  /** One event's own template (its override, or null when it inherits). */
  loadEvent = (id: string) => {
    if (!id) return;
    const seq = (this.seq.events[id] = (this.seq.events[id] || 0) + 1);
    const prev = this.state.events[id];
    this.set({ events: { ...this.state.events, [id]: { status: "loading", data: prev?.data } } });
    readEventTemplate(id)
      .then((data) => {
        if (seq === this.seq.events[id]) this.set({ events: { ...this.state.events, [id]: { status: "ready", data } } });
      })
      .catch((err) => {
        if (seq === this.seq.events[id]) this.set({ events: { ...this.state.events, [id]: { status: "error", data: prev?.data, message: toTemplateError(err).message } } });
      });
  };

  private ensureEvent(id: string) {
    if (id && !this.state.events[id]) this.loadEvent(id);
  }

  /* ── The person's choices ──────────────────────────────────────────── */

  /** An unapplied pick belongs to the scope it was made in, so changing scope drops it. */
  setLandingScope = (scope: LandingScope) => {
    if (this.state.applying || scope === this.state.landing.scope) return;
    this.set({ landing: { ...this.state.landing, scope, pick: null }, applyError: null });
  };

  /**
   * Choose the event. For "One event" that is the event being given a template, and a pick
   * made for another event is dropped. For "All events" it is only the example the default is
   * shown with, so the proposed default stays picked.
   */
  setEventId = (eventId: string) => {
    if (this.state.applying || eventId === this.state.landing.eventId) return;
    const keepPick = this.state.landing.scope === "default";
    this.set({ landing: { ...this.state.landing, eventId, pick: keepPick ? this.state.landing.pick : null } });
    this.ensureEvent(eventId);
  };

  /** Pick a dropdown value. It belongs to the context it was made in and applies nothing. */
  setChoice = (value: string | null) => {
    const sel = this.selection();
    if (!sel || this.state.applying) return;
    const pick = value === null || value === sel.appliedValue ? null : { context: contextOf(sel), value };
    this.set(sel.tab === "events" ? { list: { ...this.state.list, pick }, applyError: null } : { landing: { ...this.state.landing, pick }, applyError: null });
  };

  /** Show the standard designer or the custom template in the right-hand preview. */
  setView = (view: ViewPreference) => {
    const tab = this.state.tab;
    if (!tab) return;
    this.set(tab === "events" ? { list: { ...this.state.list, view } } : { landing: { ...this.state.landing, view } });
  };

  /** Ask the server for the current preview again (keeps the shown page, marked stale, meanwhile). */
  retryPreview = () => {
    this.seq.preview++;
    this.set({ preview: { ...this.state.preview, requestKey: null } });
  };

  clearApplyError = () => { if (this.state.applyError) this.set({ applyError: null }); };

  /* ── Derived ───────────────────────────────────────────────────────── */

  titleOf = (slug: string | null | undefined): string | null => {
    if (!slug) return null;
    const row = this.state.pages.find((p) => p.slug === slug);
    return row ? row.title : slug;
  };

  /** Published templates for the current tab's kind. */
  candidates(): PortalExperiencePageSummary[] {
    return templateCandidates(this.state.pages, this.state.tab === "landing" ? "event-detail" : "page");
  }

  eligible(slug: string | null | undefined): boolean {
    return !!slug && this.candidates().some((c) => c.slug === slug);
  }

  /** Whether the server has said, for every page, whether it is live (an older server does not). */
  private liveKnown(): boolean {
    return this.state.reads.pages.status === "ready" && listCarriesLiveLayout(this.state.pages);
  }

  target(): TemplateTarget | null {
    const tab = this.state.tab;
    return tab === "events" ? "list" : tab === "landing" ? (this.state.landing.scope === "default" ? "default" : "event") : null;
  }

  /** The current typed selection, or null away from the two template tabs. */
  selection(): TemplateSelection | null {
    const st = this.state;
    const tab = st.tab;
    const target = this.target();
    if (!tab || !target) return null;
    const settings = st.settings;
    const defaultSlug = settings?.events.detailTemplate ?? null;
    const event = tab === "landing" ? (st.catalog.events.find((e) => e.id === st.landing.eventId) || null) : null;
    const eventRead = event ? st.events[event.id] : undefined;
    const eventKnown = eventRead?.status === "ready" || !!eventRead?.data;
    const eventOwn = eventKnown ? (eventRead?.data?.portalDetailTemplate ?? null) : null;
    const appliedSlug = target === "list" ? settings?.events.listTemplate ?? null
      : target === "default" ? defaultSlug : eventOwn;
    const appliedValue = appliedSlug || (target === "event" ? INHERIT_VALUE : STANDARD_VALUE);
    const pickRec = tab === "events" ? st.list.pick : st.landing.pick;
    const value = pickRec && pickRec.context === contextKey(tab, target, event?.id ?? "") ? pickRec.value : appliedValue;
    const effectiveOf = (v: string): string | null => (v === STANDARD_VALUE ? null : v === INHERIT_VALUE ? defaultSlug : v);
    /* Settings answered (a later failed refresh keeps the last answer), and for one event its own read too. */
    const ready = !!settings && (target !== "event" || eventKnown);
    const inUse = !ready ? null : target === "list" ? appliedSlug : target === "default" ? defaultSlug : (eventOwn || defaultSlug);
    return {
      tab, target, event,
      eventRole: tab === "landing" ? (target === "event" ? "event" : "exemplar") : null,
      value, appliedValue, appliedSlug,
      effectiveSlug: effectiveOf(value),
      inUseSlug: inUse,
      inUseAvailable: !inUse || !this.liveKnown() || this.eligible(inUse),
      candidate: value !== appliedValue,
      exemplarOverride: target === "default" ? eventOwn : null,
      ready,
    };
  }

  private viewPreference(): ViewPreference {
    const tab = this.state.tab;
    return !tab ? "auto" : tab === "events" ? this.state.list.view : this.state.landing.view;
  }

  /** Whether the canvas shows the custom template for this selection. */
  private showsCustom(sel: TemplateSelection | null): boolean {
    if (!sel || !sel.effectiveSlug) return false;
    const pref = this.viewPreference();
    /* "auto" shows the custom template whenever one is assigned here, so a saved choice opens
       on its own preview without anyone pressing Apply again. */
    return pref === "custom" || (pref === "auto" && sel.ready && !!sel.inUseSlug);
  }

  /** Everything the canvas and header read, recomputed only when the state changed. */
  view = (): TemplatePreviewView => {
    if (this.viewCache && this.viewCache.state === this.state) return this.viewCache.view;
    const st = this.state;
    const sel = this.selection();
    const mode: "standard" | "custom" = this.showsCustom(sel) ? "custom" : "standard";
    const notPublished = !!sel && !!sel.effectiveSlug && this.liveKnown() && !this.eligible(sel.effectiveSlug);
    const req = mode === "custom" ? this.previewRequest() : null;
    const slot = st.preview;
    const current = !!req && slot.requestKey === req.requestKey;
    const answer = current && slot.status === "ready" ? slot.answer : null;
    const good = req && slot.lastGood && slot.lastGood.contextKey === req.contextKey ? slot.lastGood.result : null;
    const html = answer && answer.html ? answer.html : good ? good.html : null;
    const view: TemplatePreviewView = {
      mode, customAvailable: !!sel && !!sel.effectiveSlug, selection: sel, notPublished,
      preview: {
        status: !req ? (st.data.status === "loading" && !st.settings ? "loading" : "idle") : current ? slot.status : "loading",
        html,
        stale: !!html && !(answer && answer.html),
        diagnostics: answer && (answer.errors.length || !answer.html) ? { errors: answer.errors, composed: !!answer.html } : null,
        message: current && slot.status === "error" ? slot.message : null,
        needsEvent: !!sel && sel.tab === "landing" && !!sel.effectiveSlug && !sel.event,
      },
      templateTitle: this.titleOf,
    };
    this.viewCache = { state: st, view };
    return view;
  };

  /** The preview the current selection asks for, or null when there is nothing to compose. */
  private previewRequest(): PreviewRequest | null {
    const st = this.state;
    const sel = this.selection();
    if (!sel || !sel.effectiveSlug || !st.settings) return null;
    if (st.reads.pages.status !== "ready" && !st.pages.length) return null;
    if (sel.tab === "landing" && !sel.event) return null;
    if (this.liveKnown() && !this.eligible(sel.effectiveSlug)) return null;
    const slot = sel.tab === "events" ? EVENT_LIST_SLOT : sel.effectiveSlug;
    const eventId = sel.event?.id ?? "";
    const eventSlug = sel.event ? sel.event.slug : null;
    return {
      requestKey: JSON.stringify([sel.tab, sel.target, eventId, slot, sel.effectiveSlug, eventSlug, st.revision]),
      contextKey: JSON.stringify([sel.tab, sel.target, eventId, sel.effectiveSlug]),
      slot, templateSlug: sel.effectiveSlug, eventSlug,
    };
  }

  /** The request the canvas needs right now: only while it shows the custom template. */
  private wantedRequest(): PreviewRequest | null {
    return this.showsCustom(this.selection()) ? this.previewRequest() : null;
  }

  /**
   * Keep the preview in step with the selection. A context that wants no preview drops any
   * request in flight; a new key starts a new request. Runs inside set(), whose listeners
   * fire right after, so it assigns the state directly.
   */
  private syncPreview() {
    const slot = this.state.preview;
    const req = this.wantedRequest();
    if (!req) {
      if (slot.requestKey !== null) {
        this.seq.preview++;
        this.state = { ...this.state, preview: { ...PREVIEW_IDLE, lastGood: slot.lastGood } };
      }
      return;
    }
    if (slot.requestKey === req.requestKey) return;
    const seq = ++this.seq.preview;
    const keepGood = slot.lastGood && slot.lastGood.contextKey === req.contextKey ? slot.lastGood : null;
    this.state = { ...this.state, preview: { requestKey: req.requestKey, contextKey: req.contextKey, status: "loading", answer: null, message: null, lastGood: keepGood } };
    /* An answer counts only while it is the newest request AND the current selection still asks for this exact key. */
    const stillWanted = () => {
      if (seq !== this.seq.preview || this.state.preview.requestKey !== req.requestKey) return false;
      const now = this.wantedRequest();
      return !!now && now.requestKey === req.requestKey;
    };
    previewPage(req.slot, { mode: "published", templateSlug: req.templateSlug, ...(req.eventSlug ? { eventSlug: req.eventSlug } : {}) })
      .then((answer) => {
        if (!stillWanted()) return;
        const lastGood = answer.html ? { contextKey: req.contextKey, result: answer } : this.state.preview.lastGood;
        this.set({ preview: { ...this.state.preview, status: "ready", answer, message: null, lastGood } });
      })
      .catch((err) => {
        if (!stillWanted()) return;
        this.set({ preview: { ...this.state.preview, status: "error", answer: null, message: toTemplateError(err).message } });
      });
  }

  /* ── Apply (asked for and confirmed by the panel) ──────────────────── */

  /** The snapshot an Apply of the current dropdown value would send, or null when nothing would change. */
  applySnapshot(labelOf: (value: string, target: TemplateTarget) => string): ApplySnapshot | null {
    const sel = this.selection();
    if (!sel || !sel.candidate) return null;
    const slug = sel.value === STANDARD_VALUE || sel.value === INHERIT_VALUE ? null : sel.value;
    const eventRead = sel.event ? this.state.events[sel.event.id] : undefined;
    return {
      target: sel.target,
      eventId: sel.event?.id ?? "",
      eventName: sel.event?.name || eventRead?.data?.title || "",
      slug, label: labelOf(sel.value, sel.target),
    };
  }

  /**
   * Send one confirmed snapshot, read the canonical value back and compare. Only a matching
   * read-back is reported as done; anything else keeps the pick on screen with the reason.
   * The read-back is authoritative: older reads of the same thing still in flight are
   * invalidated, and the preview is composed again against the changed data.
   */
  apply = (snap: ApplySnapshot, describe: (slug: string | null, target: TemplateTarget) => string) => {
    if (this.state.applying) return;
    this.set({ applying: snap, applyError: null });
    const finish = (actual: string | null) => {
      if (actual === snap.slug) {
        /* Clear the pick only if it is still the one that was applied. */
        const wanted = snap.slug ?? (snap.target === "event" ? INHERIT_VALUE : STANDARD_VALUE);
        const tabKey = snap.target === "list" ? "list" : "landing";
        const pick = this.state[tabKey].pick;
        const keep = pick && pick.value !== wanted ? pick : null;
        this.set({ applying: null, ...(tabKey === "list" ? { list: { ...this.state.list, pick: keep } } : { landing: { ...this.state.landing, pick: keep } }) });
        toast({
          tone: "success",
          title: snap.target === "list" ? i18nT("journeys.customTemplates.listApplied", "Event list template updated")
            : snap.target === "default" ? i18nT("journeys.customTemplates.defaultApplied", "Default event template updated")
              : tf("journeys.customTemplates.eventApplied", "Template updated for {event}", { event: snap.eventName }),
        });
      } else {
        this.set({
          applying: null,
          applyError: {
            snapshot: snap,
            message: tf("journeys.customTemplates.conflict", "It was saved, but it now reads “{actual}”. Someone else may have changed it at the same time.", {
              actual: describe(actual, snap.target),
            }),
          },
        });
      }
    };
    const fail = (err: unknown) => this.set({ applying: null, applyError: { snapshot: snap, message: toTemplateError(err).message } });
    const commitSettings = (s: PortalSettings) => {
      this.seq.settings++;
      this.setReads({ settings: { status: "ready" } }, { settings: s, revision: this.state.revision + 1 });
    };
    if (snap.target === "list") {
      applyListTemplate(snap.slug).then((s) => { commitSettings(s); finish(s.events.listTemplate); }).catch(fail);
    } else if (snap.target === "default") {
      applyDefaultDetailTemplate(snap.slug).then((s) => { commitSettings(s); finish(s.events.detailTemplate); }).catch(fail);
    } else {
      applyEventDetailTemplate(snap.eventId, snap.slug).then((data) => {
        this.seq.events[snap.eventId] = (this.seq.events[snap.eventId] || 0) + 1;
        this.set({ events: { ...this.state.events, [snap.eventId]: { status: "ready", data } }, revision: this.state.revision + 1 });
        finish(data.portalDetailTemplate);
      }).catch(fail);
    }
  };
}

function contextKey(tab: TemplateTab, target: TemplateTarget, eventId: string): string {
  return JSON.stringify([tab, target, target === "event" ? eventId : ""]);
}

function contextOf(sel: TemplateSelection): string {
  return contextKey(sel.tab, sel.target, sel.event?.id ?? "");
}

/** Subscribe a function component to a controller (or to none). */
export function useTemplatePreviewController(controller: TemplatePreviewController | null): TemplateControllerState | null {
  const subscribe = React.useCallback((fn: () => void) => (controller ? controller.subscribe(fn) : () => {}), [controller]);
  const get = React.useCallback(() => (controller ? controller.getState() : null), [controller]);
  return React.useSyncExternalStore(subscribe, get, get);
}
